import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Link } from 'react-router-dom';

import { useAppContext } from './AppContext.jsx';
import es from '../locales/cookieConsent_es.json';
import en from '../locales/cookieConsent_en.json';
import {
    applyConsent,
    DEFAULT_CONSENT,
    getGpcDefaultConsent,
    getStoredConsent,
    hasGpcSignal,
    saveConsent,
} from '../lib/cookieConsentStorage.js';

const CookieConsentContext = createContext();

const buildConsent = (overrides, source) => ({
    ...DEFAULT_CONSENT,
    ...overrides,
    essential: true,
    source,
});

function CookieToggle({ id, checked, disabled, onChange, label }) {
    return (
        <label className="cookie-toggle" htmlFor={id}>
            <input
                id={id}
                type="checkbox"
                className="cookie-toggle-input"
                checked={checked}
                disabled={disabled}
                onChange={(event) => onChange(event.target.checked)}
            />
            <span className="cookie-toggle-track" aria-hidden="true">
                <span className="cookie-toggle-thumb" />
            </span>
            <span className="sr-only">{label}</span>
        </label>
    );
}

function CookieCategory({ title, description, alwaysOnLabel, toggle }) {
    return (
        <div className="cookie-category">
            <div className="cookie-category-header">
                <h3>{title}</h3>
                {toggle ?? (
                    <span className="cookie-category-badge">{alwaysOnLabel}</span>
                )}
            </div>
            <p>{description}</p>
        </div>
    );
}

function renderBannerDescription(text) {
    const parts = text.split(/<privacyLink>|<\/privacyLink>/);
    if (parts.length === 1) return text;

    return parts.map((part, index) => (
        index % 2 === 1
            ? <Link key={index} to="/privacy">{part}</Link>
            : <React.Fragment key={index}>{part}</React.Fragment>
    ));
}

export function CookieConsentProvider({ children }) {
    const [consent, setConsent] = useState(() => getStoredConsent());
    const [showBanner, setShowBanner] = useState(() => !getStoredConsent());
    const [showPreferences, setShowPreferences] = useState(false);
    const [draft, setDraft] = useState(() => getStoredConsent() ?? DEFAULT_CONSENT);

    const commitConsent = useCallback((nextConsent) => {
        const saved = saveConsent(nextConsent);
        setConsent(saved);
        setDraft(saved);
        setShowBanner(false);
        setShowPreferences(false);
        applyConsent(saved);
    }, []);

    useEffect(() => {
        const stored = getStoredConsent();
        if (stored) {
            applyConsent(stored);
            return;
        }

        if (hasGpcSignal()) {
            commitConsent(getGpcDefaultConsent());
        }
    }, [commitConsent]);

    const acceptAll = useCallback(() => {
        commitConsent(buildConsent({
            analytics: true,
            marketing: true,
            doNotSell: false,
        }, 'banner'));
    }, [commitConsent]);

    const rejectAll = useCallback(() => {
        commitConsent(buildConsent({
            analytics: false,
            marketing: false,
            doNotSell: true,
        }, 'banner'));
    }, [commitConsent]);

    const savePreferences = useCallback(() => {
        const nextConsent = buildConsent({
            analytics: draft.analytics,
            marketing: draft.doNotSell ? false : draft.marketing,
            doNotSell: draft.doNotSell,
        }, 'preferences');
        commitConsent(nextConsent);
    }, [commitConsent, draft]);

    const openPreferences = useCallback(() => {
        setDraft(consent ?? DEFAULT_CONSENT);
        setShowPreferences(true);
    }, [consent]);

    const closePreferences = useCallback(() => {
        setShowPreferences(false);
        setDraft(consent ?? DEFAULT_CONSENT);
    }, [consent]);

    const updateDraft = useCallback((field, value) => {
        setDraft((current) => {
            const next = { ...current, [field]: value };
            if (field === 'doNotSell' && value) {
                next.marketing = false;
            }
            return next;
        });
    }, []);

    useEffect(() => {
        if (!showPreferences) return undefined;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                closePreferences();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showPreferences, closePreferences]);

    const contextValue = useMemo(() => ({
        consent,
        showBanner,
        showPreferences,
        acceptAll,
        rejectAll,
        savePreferences,
        openPreferences,
        closePreferences,
    }), [
        consent,
        showBanner,
        showPreferences,
        acceptAll,
        rejectAll,
        savePreferences,
        openPreferences,
        closePreferences,
    ]);

    return (
        <CookieConsentContext.Provider value={contextValue}>
            {children}
            <CookieConsentUI
                draft={draft}
                updateDraft={updateDraft}
            />
        </CookieConsentContext.Provider>
    );
}

function CookieConsentUI({ draft, updateDraft }) {
    const { lang, getLanguageObject } = useAppContext();
    const t = getLanguageObject(lang, es, en);
    const {
        showBanner,
        showPreferences,
        acceptAll,
        rejectAll,
        savePreferences,
        openPreferences,
        closePreferences,
    } = useCookieConsent();

    if (!showBanner && !showPreferences) return null;

    return (
        <>
            {showPreferences && (
                <div
                    className="cookie-consent-overlay"
                    role="presentation"
                    onClick={closePreferences}
                />
            )}

            <div
                className={`cookie-consent ${showPreferences ? 'cookie-consent--modal' : ''}`}
                role="dialog"
                aria-modal={showPreferences ? 'true' : 'false'}
                aria-labelledby="cookie-consent-title"
                aria-describedby="cookie-consent-description"
            >
                <div className="cookie-consent-inner">
                    <h2 id="cookie-consent-title">
                        {showPreferences ? t.preferencesTitle : t.bannerTitle}
                    </h2>
                    <p id="cookie-consent-description" className="cookie-consent-description">
                        {showPreferences
                            ? t.preferencesDescription
                            : renderBannerDescription(t.bannerDescription)}
                    </p>

                    {showPreferences && (
                        <div className="cookie-consent-categories">
                            <CookieCategory
                                title={t.essentialTitle}
                                description={t.essentialDescription}
                                alwaysOnLabel={t.alwaysOn}
                            />
                            <CookieCategory
                                title={t.analyticsTitle}
                                description={t.analyticsDescription}
                                toggle={(
                                    <CookieToggle
                                        id="cookie-analytics"
                                        label={t.analyticsTitle}
                                        checked={draft.analytics}
                                        onChange={(value) => updateDraft('analytics', value)}
                                    />
                                )}
                            />
                            <CookieCategory
                                title={t.marketingTitle}
                                description={t.marketingDescription}
                                toggle={(
                                    <CookieToggle
                                        id="cookie-marketing"
                                        label={t.marketingTitle}
                                        checked={draft.marketing}
                                        disabled={draft.doNotSell}
                                        onChange={(value) => updateDraft('marketing', value)}
                                    />
                                )}
                            />
                            <div className="cookie-category cookie-category--ccpa">
                                <div className="cookie-category-header">
                                    <h3>{t.doNotSellTitle}</h3>
                                    <CookieToggle
                                        id="cookie-do-not-sell"
                                        label={t.doNotSellTitle}
                                        checked={draft.doNotSell}
                                        onChange={(value) => updateDraft('doNotSell', value)}
                                    />
                                </div>
                                <p>{t.doNotSellDescription}</p>
                            </div>
                        </div>
                    )}

                    <div className="cookie-consent-actions">
                        {showPreferences ? (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-secondary cookie-consent-btn"
                                    onClick={closePreferences}
                                >
                                    {t.close}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary cookie-consent-btn"
                                    onClick={rejectAll}
                                >
                                    {t.rejectAll}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary cookie-consent-btn"
                                    onClick={savePreferences}
                                >
                                    {t.savePreferences}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-secondary cookie-consent-btn"
                                    onClick={rejectAll}
                                >
                                    {t.rejectAll}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary cookie-consent-btn"
                                    onClick={openPreferences}
                                >
                                    {t.managePreferences}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary cookie-consent-btn"
                                    onClick={acceptAll}
                                >
                                    {t.acceptAll}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export const useCookieConsent = () => useContext(CookieConsentContext);

export function CookieSettingsLink() {
    const { lang, getLanguageObject } = useAppContext();
    const t = getLanguageObject(lang, es, en);
    const { openPreferences } = useCookieConsent();

    return (
        <button
            type="button"
            className="cookie-settings-link"
            onClick={openPreferences}
        >
            {t.footerLink}
        </button>
    );
}
